import { useEffect } from 'react'

import {
  Modal,
  Divider,
  Select,
  TextInput,
  RadioGroup,
  Radio,
  useModalStackContext,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import Button from 'src/components/Buttons/Button/Button'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import { TERMINATED_REASON_OPTIONS } from 'src/lib/terminatedReason.utils'

const STACK_ID = 'terminated-modal'

const TerminatedModal = ({ onSave, onClose }) => {
  const stackCtx = useModalStackContext()

  // Mantine gap: Modal's stack-register useEffect has no cleanup, so
  // unmounting while opened leaks the stackId in Modal.Stack. Mirrors DeleteModal.
  useEffect(() => {
    return () => {
      if (stackCtx) {
        stackCtx.removeModal(STACK_ID)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const schema = z.object({
    terminatedReason: z
      .union([z.string(), z.null()])
      .refine((val) => val !== null && val.trim().length > 0, {
        message: 'Reason is required',
      }),
    eligibleForRehire: z
      .string()
      .refine((val) => val === 'true' || val === 'false', {
        message: 'Permanently disqualified status is required',
      }),
    content: z.string().min(0).max(250),
  })
  const form = useForm({
    initialValues: {
      applicantStageDate: dayjs(),
      terminatedReason: null,
      content: '',
      eligibleForRehire: 'true',
    },
    validate: zodResolver(schema),
  })
  const handleSubmit = (values) => {
    // TODO: Include the remaining fields
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { eligibleForRehire, ...rest } = values
    onSave({
      ...rest,
      eligibleForRehire:
        eligibleForRehire === 'true' || eligibleForRehire === true,
    })
    onClose()
  }

  const reasons = TERMINATED_REASON_OPTIONS

  return (
    <Modal
      opened={true}
      onClose={onClose}
      classNames={{ title: 'text-lg font-bold' }}
      title="Terminated"
      centered
      stackId={STACK_ID}
    >
      <form
        className="grid grid-cols-2 gap-4"
        onSubmit={form.onSubmit(handleSubmit)}
      >
        <DateInput
          label="Termination Date"
          value={form.values.applicantStageDate?.toDate?.() ?? form.values.applicantStageDate}
          onChange={(date) => form.setFieldValue('applicantStageDate', date)}
        />
        <Select
          label="Reason"
          placeholder="Select a reason"
          data={reasons}
          searchable
          withAsterisk
          {...form.getInputProps('terminatedReason')}
        />
        <RadioGroup
          className="col-span-2 gap-4"
          label="Eligible for rehire?"
          withAsterisk
          {...form.getInputProps('eligibleForRehire')}
        >
          <div className="flex items-center gap-4">
            <Radio
              label="Yes"
              value="true"
              size="xs"
              classNames={{
                label: 'text-sm',
                radio: 'checked:bg-doubleNickel-brand-500',
              }}
            />
            <Radio
              label="No"
              value="false"
              size="xs"
              classNames={{
                label: 'text-sm',
                radio: 'checked:bg-doubleNickel-brand-500',
              }}
            />
          </div>
        </RadioGroup>
        <TextInput
          label="Notes"
          placeholder="Additional details or comments (optional)"
          className="col-span-2"
          {...form.getInputProps('content')}
        />
        <Divider className="col-span-2" />
        <Button text="Cancel" variant="outline" onClick={onClose} />
        <Button text="Save" type="submit" />
      </form>
    </Modal>
  )
}

export default TerminatedModal
